import { useEffect, useState } from "react";
import { Check, Crosshair, X } from "lucide-react";
import { useAuth } from "../auth/useAuth";
import { buildElementContext, pickElement, pickerBusy } from "../lib/bugPicker";
import { BATCH_CAP, bugMode, useBugMode, type RedType } from "../lib/bugMode";
import { submitRed } from "../lib/tracker";

// Global overlay for RED Mode — the admin's point-and-click work-order generation mode (evolved from
// the Phase 1.5 bug-report mode). RED = Requirements / Enhancements / Defects. The viewport frame +
// badge make the mode unmistakable; ⌥-click (or the tray's one-shot picker) pins an element; each pin
// is classified R/E/D with a note; the session submits as ONE work order (bundled members) or as
// individual r/d/e items. Admin-only, device-local. Pinning is deliberate (never plain click) so
// navigation and panels keep working across a multi-page session.

const RED_TYPES: { key: RedType; label: string }[] = [
  { key: "requirement", label: "Req" },
  { key: "enhancement", label: "Enh" },
  { key: "defect", label: "Def" },
];

/** Brief flash on the pinned element so ⌥-click gives immediate feedback. */
function flash(el: Element) {
  const r = el.getBoundingClientRect();
  const d = document.createElement("div");
  d.className = "bugpick-outline";
  d.style.cssText = `display:block;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;transition:opacity .5s`;
  document.body.append(d);
  window.setTimeout(() => (d.style.opacity = "0"), 150);
  window.setTimeout(() => d.remove(), 700);
}

export function BugReviewMode() {
  const { isAdmin, getApiToken } = useAuth();
  const { on, annotations } = useBugMode();
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!on || !isAdmin) return;
    const onClick = (e: MouseEvent) => {
      if (!e.altKey) return;
      // Stand down while the one-shot picker owns the gesture (and its trailing click),
      // or one ⌥-click would pin twice.
      if (pickerBusy()) return;
      const t = e.target;
      if (!(t instanceof Element) || t.closest(".bugmode-tray, .bugmode-badge")) return;
      e.preventDefault();
      e.stopPropagation();
      if (bugMode.pinContext(buildElementContext(t))) flash(t);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [on, isAdmin]);

  if (!isAdmin || !on) return null;

  const addPin = () => {
    void pickElement().then((ctx) => {
      if (ctx) bugMode.pinContext(ctx);
    });
  };

  const submit = (asWorkOrder: boolean) => {
    setSubmitting(true);
    setMsg(null);
    const ids = annotations.map((a) => a.id);
    const pins = annotations.map((a) => ({ type: a.type, msg: a.msg, context: a.context }));
    const n = ids.length;
    void (async () => {
      try {
        const token = await getApiToken();
        const ok = token
          ? await submitRed(token, { asWorkOrder, title: title.trim(), pins })
          : false;
        if (ok) {
          // clear only what we submitted — a pin added mid-flight survives.
          bugMode.clearIds(ids);
          setTitle("");
          setMsg(
            asWorkOrder
              ? `Created a work order from ${n} ${n === 1 ? "pin" : "pins"}.`
              : `Filed ${n} ${n === 1 ? "item" : "items"}.`,
          );
        } else {
          // keep the pins for retry
          setMsg("Submit failed — pins kept, try again.");
        }
      } catch {
        setMsg("Submit failed — pins kept, try again.");
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const done = msg?.startsWith("Created") || msg?.startsWith("Filed");

  return (
    <>
      <div className="bugmode-frame print:hidden" aria-hidden />
      <div className="bugmode-badge mono print:hidden">RED Mode · ⌥-click pins an element</div>
      <aside className="bugmode-tray print:hidden" data-c="BugReviewMode" aria-label="RED Mode session">
        <div className="bugmode-tray-head">
          <span className="mono">Pins ({annotations.length})</span>
          <div className="bugmode-tray-btns">
            <button type="button" className="btn btn-sm" onClick={addPin}>
              <Crosshair size={13} /> Add pin
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Exit RED Mode"
              onClick={() => bugMode.toggle(false)}
            >
              <X size={16} />
            </button>
          </div>
        </div>
        {annotations.length === 0 && (
          <p className="bugmode-empty mono">
            ⌥-click any element (or Add pin) to start collecting. Navigate freely — the session
            spans pages.
          </p>
        )}
        {annotations.length >= BATCH_CAP && (
          <p className="bugmode-empty mono">
            Pin limit reached ({BATCH_CAP}) — file these, then start another.
          </p>
        )}
        {annotations.length > 0 && (
          <ol className="bugmode-list">
            {annotations.map((a, i) => (
              <li key={a.id} className="bugmode-pin">
                <div className="bugmode-pin-head">
                  <span className="bugmode-pin-num mono">{i + 1}</span>
                  <span className="bugmode-pin-what mono">
                    {a.context?.component ?? a.context?.tag ?? "?"} · {a.context?.route ?? "?"}
                  </span>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Remove pin ${i + 1}`}
                    onClick={() => bugMode.remove(a.id)}
                  >
                    <X size={13} />
                  </button>
                </div>
                <div className="bugmode-pin-types" role="group" aria-label="Item type">
                  {RED_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`bugmode-type mono${a.type === t.key ? " is-on" : ""}`}
                      aria-pressed={a.type === t.key}
                      onClick={() => bugMode.setType(a.id, t.key)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <input
                  className="jfb-input"
                  placeholder="What's needed here?"
                  value={a.msg}
                  onChange={(e) => bugMode.setMsg(a.id, e.target.value)}
                />
              </li>
            ))}
          </ol>
        )}
        <div className="bugmode-tray-foot">
          {msg && (
            <span className="mono bugmode-msg">
              {done && <Check size={12} />} {msg}
            </span>
          )}
          {annotations.length > 0 && (
            <div className="bugmode-submit">
              <div className="bugmode-wo">
                <input
                  className="jfb-input"
                  placeholder="Work order title (optional)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={submitting}
                  onClick={() => submit(true)}
                >
                  Create work order
                </button>
              </div>
              <button
                type="button"
                className="btn btn-sm bugmode-file-btn"
                disabled={submitting}
                onClick={() => submit(false)}
              >
                File as items
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
